import React, {useEffect, useState} from 'react';
import {Badge, Button, Col, Dropdown, Form, Modal, Row} from 'react-bootstrap';
import {Department, DEPARTMENTS, IConflictCheckResponse, IEmployee, IEvent, IFirestoreProductionResource, IProductionZone, ITraveler, ProductionEventColor} from '../data/interfaces';
import {fetchTravelers} from '../data/fetchTravelers';
import {db} from '../firebaseConfig';
import {collection, doc} from 'firebase/firestore';
import {eventColors, EventType, getColor} from '../utils/eventColors';
import {formatDateForInput} from "../utils/formatDateForInput";
import {buildEventTitle} from "../utils/buildEventTitle";
import {userCanEdit} from "../utils/permissions";
import {getEmployeeFromUid} from "../data/getEmployeeFromUid";
import Select from 'react-select';
import {ADJUSTED_DEADLINE_LABEL, ADJUSTED_DEADLINE_TOOLTIP, formatAdjustedDeadline} from "../utils/adjustedDeadline";
import {firestoreCollections} from "../data/firestoreConstants";
import {getProductionEventColor, productionEventColors} from "../utils/productionEventColors";

interface EventModalProps {
  event: IEvent | null;
  show: boolean;
  handleCloseModal: () => void;
  handleSave: (event: IEvent) => Promise<void>;
  handleDuplicate: (event: IEvent) => Promise<void>;
  checkForConflicts: (event: IEvent) => IConflictCheckResponse;
  department: Department;
  availableCrews: IEmployee[];
  productionResources: IFirestoreProductionResource[];
  selectedProductionZone?: IProductionZone | null;
  defaultEventDate?: string;
  deleteEvent: (event: IEvent) => void;
  employeeData: IEmployee | null;
  travelerId?: string; // Add this line
}

const EventModal: React.FC<EventModalProps> = ({
                                                 event,
                                                 show,
                                                 handleCloseModal,
                                                 handleDuplicate,
                                                 handleSave,
                                                 checkForConflicts,
                                                 department,
                                                 availableCrews,
                                                 productionResources,
                                                 selectedProductionZone,
                                                 defaultEventDate = '',
                                                 deleteEvent,
                                                 employeeData,
                                                 travelerId,
                                               }) => {

  // State for the local event; initializes with the 'event' prop or default values
  const [localEvent, setLocalEvent] = useState<IEvent>(
    event || {
      id: '',
      airtableRecordId: '',
      start: defaultEventDate,
      end: defaultEventDate,
      crew: null,
      department,
      traveler: null,
      notes: '',
      customText: '',
      eventType: null,
      color: '',
      productionResourceId: null,
      productionEventColor: null,
    }
  );

  // State to hold travelers fetched from Firestore
  const [travelers, setTravelers] = useState<ITraveler[]>([]);
  useEffect(() => {
    if (travelerId) {
      const selectedTraveler = travelers.find(traveler => traveler.id === travelerId);
      setLocalEvent(prevState => ({
        ...prevState,
        traveler: selectedTraveler || null,
      }));
    }
  }, [travelerId, travelers]);
  /**
   * Synchronize 'localEvent' state with 'event' prop when 'event' changes.
   */
  useEffect(() => {
    if (show && !event) {
      // Reset the localEvent state when the modal is opened for a new event
      setLocalEvent({
        id: '',
        airtableRecordId: '',
        start: defaultEventDate,
        end: defaultEventDate,
        crew: null,
        department,
        traveler: null,
        notes: '',
        customText: '',
        eventType: null,
        color: '',
        productionResourceId: null,
        productionEventColor: null,
      });
    } else if (event) {
      setLocalEvent(event);
    }
    // console.log('Local event state:', localEvent); // Log local event state
  }, [show, event, department, defaultEventDate]);

  /**
   * Fetch travelers whenever the modal is shown or when the department changes.
   */
  useEffect(() => {
    if (show) {
      const loadTravelers = async () => {
        try {
          const fetchedTravelers = await fetchTravelers(department, selectedProductionZone);
          const sortedTravelers = fetchedTravelers.sort((a, b) =>
            a.displayName.localeCompare(b.displayName)
          );
          setTravelers(sortedTravelers);
        } catch (error) {
          console.error('Error fetching travelers:', error);
        }
      };
      loadTravelers();
    } else {
      // Optional: Reset travelers when the modal is closed
      setTravelers([]);
    }
  }, [show, department, selectedProductionZone]);

  /**
   * Handle changes in form inputs for simple fields.
   */
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = event.target;
    setLocalEvent((previousEvent) => {
      if (
        name === 'start' &&
        previousEvent.end &&
        value > formatDateForInput(previousEvent.end)
      ) {
        return {...previousEvent, start: value, end: value};
      }

      return {...previousEvent, [name]: value};
    });
  };

  /**
   * Adding traveler option to the select dropdown
   */
  const travelerOptions = travelers.map((traveler) => ({
    value: traveler.id,
    label: traveler.displayName,
  }));

  /**
   * Handle changes in form inputs for the traveler dropdown.
   */
  const handleReactSelectChange = (newValue: { value: string; label: string } | null) => {
    if (newValue) {
      const syntheticEvent = {
        target: {
          value: newValue.value,
        },
      } as React.ChangeEvent<HTMLSelectElement>; // Cast to the correct type
      handleTravelerChange(syntheticEvent);
    } else {
      const syntheticEvent = {
        target: {
          value: "",
        },
      } as React.ChangeEvent<HTMLSelectElement>;
      handleTravelerChange(syntheticEvent);
    }
  };

  /**
   * Handle traveler selection.
   * Updates the 'traveler' field in 'localEvent' based on selected traveler ID.
   */
  const handleTravelerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTravelerId = e.target.value;
    const selectedTraveler = travelers.find(
      (traveler) => traveler.id === selectedTravelerId
    );
    setLocalEvent((prevState) => ({
      ...prevState,
      traveler: selectedTraveler || null,
    }));
  };

  /**
   * Handle crew selection.
   * Updates the 'crew' field in 'localEvent' based on selected crew member ID.
   */
  const handleCrewChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCrewId = e.target.value;
    const selectedCrew = availableCrews.find(
      (crewMember) => crewMember.id === selectedCrewId
    );
    setLocalEvent((prevState) => ({
      ...prevState,
      crew: selectedCrew || null,
      resourceId: selectedCrew?.preferredName || 'undefined',
    }));
  };

  const handleProductionResourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedProductionResourceId = e.target.value;
    setLocalEvent((prevState) => ({
      ...prevState,
      productionResourceId: selectedProductionResourceId || null,
      crew: null,
    }));
  };

  const handleProductionEventColorChange = (selectedProductionEventColor: string | null) => {
    setLocalEvent((prevState) => ({
      ...prevState,
      productionEventColor: selectedProductionEventColor
        ? selectedProductionEventColor as ProductionEventColor
        : null,
    }));
  };

  const handleEventTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedEventType = e.target.value as EventType;
    const {backgroundColor} = getColor(selectedEventType);

    setLocalEvent((prevState) => ({
      ...prevState,
      eventType: selectedEventType,
      color: backgroundColor,
      department: selectedEventType === "Holiday" ? 'all' : department, // Set department to 'all' for holidays
      productionEventColor: selectedEventType === "Holiday" ? null : prevState.productionEventColor,
      traveler:
        selectedEventType === "Holiday"
        || selectedEventType === "No PO (pending)"
        || selectedEventType === "Day Off"
        || selectedEventType === "Team Meeting"
          ? null
          : prevState.traveler, // Clear traveler for holidays
      crew:
        selectedEventType === "Holiday"
        || selectedEventType === "Team Meeting"
          ? null
          : prevState.crew, // Clear crew for holidays
    }));
  };

  /**
   * Handle form submission.
   * Generates a new ID if necessary and invokes 'handleSave' and 'handleCloseModal'.
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!userCanEdit(localEvent.start, employeeData, department)) {
      alert('You do not have permission to save or edit this event.');
      return;
    }

    // Check for event conflicts before saving
    const {safeToSave, lastModifiedBy} = checkForConflicts(localEvent);

    if (!safeToSave) {
      const employee: IEmployee | null = lastModifiedBy ? await getEmployeeFromUid(lastModifiedBy.id) : null;
      alert(`This event has been modified by ${employee?.preferredName ?? 'someone else'} since you started editing it. Your changes will not be saved.`);
      handleCloseModal();
      return;
    }

    // ensure that events have a meaningful title before saving
    if (buildEventTitle(localEvent).length < 3) {
      alert('Please choose a traveler or set custom text before saving!');
      return;
    }

    const eventToSave: IEvent = {...localEvent};
    // Generate a new ID if this is a new event
    if (!eventToSave.id) {
      const newDocRef = doc(collection(db, firestoreCollections.events));
      eventToSave.id = newDocRef.id;
    }

    // Optimistically close the modal while the save request is in-flight.
    handleCloseModal();

    try {
      await handleSave(eventToSave);
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  /**
   * Handle deletion of the event.
   */
  const handleDelete = () => {

    if (!userCanEdit(localEvent.start, employeeData, department)) {
      alert('You do not have permission to delete this event.');
      return;
    }

    if (event && window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      deleteEvent(event);
      handleCloseModal();
    }
  };

  const localHandleDuplicate = async () => {
    if (!localEvent) return;

    // Check if the user can duplicate the event
    if (!userCanEdit(localEvent.start, employeeData, department)) {
      alert('You do not have permission to duplicate this event.');
      return;
    }

    try {
      // Save the current event first. If this fails, do not continue duplication.
      await handleSave(localEvent);
    } catch (error) {
      console.error('Error saving event before duplicate:', error);
      return;
    }

    // Create a new event object based on the saved event
    const duplicatedEvent: IEvent = {
      ...localEvent,
      id: '', // Placeholder for the new ID
    };

    // Generate a new ID for the duplicated event
    const newDocRef = doc(collection(db, firestoreCollections.events));
    duplicatedEvent.id = newDocRef.id;

    try {
      // Pass the duplicated event to the parent component
      await handleDuplicate(duplicatedEvent);
    } catch (error) {
      console.error('Error duplicating event:', error);
      return;
    }

    // Close the modal
    handleCloseModal();
  };

  return (
    <Modal show={show} onHide={handleCloseModal} size="lg" className="event-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {/*Custom style added to vertically align the badge with the center of the title. Boostrap mt-1 was too much (0.25rem).*/}
          {!localEvent.id && <Badge bg="success" className="me-2 float-start" style={{marginTop: "0.2rem"}}>New</Badge>}
          <span>{buildEventTitle(localEvent)}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {/* Display traveler details for saved events and selected unscheduled travelers. */}
          {(localEvent.id || localEvent.traveler) && (
            <Form.Group className="mb-3">
              <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                {department === DEPARTMENTS.assembly && (
                  <div title={ADJUSTED_DEADLINE_TOOLTIP}>
                    {ADJUSTED_DEADLINE_LABEL}: {formatAdjustedDeadline(localEvent.traveler?.readyToShipDeadline)}
                  </div>
                )}
                <div className="text-md-end">
                  Traveler Status: {localEvent.traveler?.status || 'N/A'}&nbsp;
                  {localEvent.traveler?.portalLink && (
                    <a href={localEvent.traveler.portalLink} target="_blank" rel="noopener noreferrer">
                      View in Portal
                    </a>
                  )}
                </div>
              </div>
            </Form.Group>
          )}
          <Row>
            <Col>
              <Form.Group controlId="startDate" className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="start"
                  value={formatDateForInput(localEvent.start)}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="endDate" className="mb-3">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  name="end"
                  value={
                    localEvent.end ? formatDateForInput(localEvent.end) : ''
                  }
                  min={formatDateForInput(localEvent.start)}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            {/* Event Type Dropdown */}
            {department !== DEPARTMENTS.assembly && department !== DEPARTMENTS.production &&
              <Col>
                <Form.Group controlId="eventType" className="mb-3">
                  <Form.Label>Event Type</Form.Label>
                  <Form.Select
                    value={localEvent.eventType || ''}
                    onChange={handleEventTypeChange}
                    required
                    style={{
                      backgroundColor: getColor(localEvent.eventType).backgroundColor,
                      color: getColor(localEvent.eventType).textColor,
                    }}
                  >
                    <option value="">Select Event Type</option>
                    {Object.keys(eventColors).map((eventType) => (
                      <option
                        key={eventType}
                        value={eventType}
                        style={{
                          backgroundColor: eventColors[eventType as EventType].backgroundColor,
                          color: eventColors[eventType as EventType].textColor,
                        }}
                      >
                        {eventType}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            }

            {/* Crew or Resource Dropdown */}
            {department === DEPARTMENTS.production && localEvent.eventType !== 'Holiday' && localEvent.eventType !== 'Team Meeting' &&
                <Col>
                  <Form.Group controlId="productionResource" className="mb-3">
                    <Form.Label>Resource</Form.Label>
                    <Form.Select
                        value={localEvent.productionResourceId || ''}
                        onChange={handleProductionResourceChange}
                    >
                      <option value="">Select a Resource</option>
                      {productionResources.map((resource) => (
                          <option key={resource.id} value={resource.id}>
                            {resource.name}
                          </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
            }
            {localEvent.department === DEPARTMENTS.production &&
                <Col>
                  <Form.Group controlId="productionEventColor" className="mb-3">
                    <Form.Label>Color</Form.Label>
                    <Dropdown className="production-event-color-dropdown" onSelect={handleProductionEventColorChange}>
                      <Dropdown.Toggle
                          className="form-select production-event-color-dropdown-toggle"
                          variant="light"
                          style={{
                            backgroundColor: getProductionEventColor(localEvent.productionEventColor).backgroundColor,
                            color: getProductionEventColor(localEvent.productionEventColor).textColor,
                          }}
                      >
                        {getProductionEventColor(localEvent.productionEventColor).label}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="production-event-color-menu">
                        <Dropdown.Item
                            active={!localEvent.productionEventColor}
                            eventKey=""
                        >
                          {getProductionEventColor(null).label}
                        </Dropdown.Item>
                        {Object.entries(productionEventColors).map(([productionEventColor, colorProperties]) => (
                            <Dropdown.Item
                                active={localEvent.productionEventColor === productionEventColor}
                                eventKey={productionEventColor}
                                key={productionEventColor}
                                style={{
                                  backgroundColor: colorProperties.backgroundColor,
                                  color: colorProperties.textColor,
                                  '--production-event-option-background': colorProperties.backgroundColor,
                                  '--production-event-option-text': colorProperties.textColor,
                                } as React.CSSProperties & {
                                  '--production-event-option-background': string;
                                  '--production-event-option-text': string;
                                }}
                            >
                              {colorProperties.label}
                            </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Form.Group>
                </Col>
            }
            {department !== DEPARTMENTS.production && localEvent.eventType !== 'Holiday' && localEvent.eventType !== 'Team Meeting' &&
                <Col>
                  <Form.Group controlId="crew" className="mb-3">
                    <Form.Label>Crew</Form.Label>
                    <Form.Select
                        value={localEvent.crew?.id || ''}
                        onChange={handleCrewChange}
                    >
                      <option value="">Select a Crew</option>
                      {availableCrews
                        .sort((a, b) =>
                          a.preferredName.localeCompare(b.preferredName)
                        )
                        .map((crewMember) => (
                          <option key={crewMember.id} value={crewMember.id}>
                            {crewMember.preferredName}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
            }
          </Row>

          {/* Traveler Dropdown */}
          {localEvent.eventType !== 'Holiday' && localEvent.eventType !== 'Day Off' && localEvent.eventType !== 'Team Meeting' && localEvent.eventType !== 'No PO (pending)' &&
              <Form.Group controlId="traveler" className="mb-3">
                <Form.Label>Traveler</Form.Label>
                <Select
                    classNamePrefix="event-modal-traveler-select"
                    value={travelerOptions.find(option => option.value === localEvent.traveler?.id) || null}
                    onChange={handleReactSelectChange}
                    options={travelerOptions}
                    placeholder="Select a Traveler"
                    isClearable
                    isSearchable
                />
              </Form.Group>
          }

          <Form.Group controlId="customText" className="mb-3">
            <Form.Label>Custom Text (added to title)</Form.Label>
            <Form.Control
              as="textarea"
              name="customText"
              value={localEvent.customText || ''}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group controlId="notes" className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              name="notes"
              value={localEvent.notes || ''}
              onChange={handleChange}
            />
          </Form.Group>

          <Row className="mt-3">
            <Col className="text-start">
              {localEvent.id &&

                  <>
                    <Button variant="outline-danger" className="me-2" onClick={handleDelete}>
                      Delete
                    </Button>
                    <Button
                      variant="outline-success"
                      onClick={(event) => {
                        if (event.currentTarget.form!.reportValidity()) {
                          void localHandleDuplicate();
                        }
                      }}
                    >
                      Save & Duplicate
                    </Button>
                  </>
              }
            </Col>
            <Col className="text-end">
              <Button
                variant="outline-primary"
                onClick={handleCloseModal}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EventModal;
